import {
  Box,
  Button,
  FormControl,
  Grid,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import _ from "lodash";
import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useQuery } from "react-query";
import { useDispatch } from "react-redux";
import { AutoComplete, Input } from "src/components/common/FormComponents";
import errorMessage from "src/constants/errorMessage";
import { formHeaders } from "src/constants/sampleData";
// import { setStep } from "src/redux/slices/newProject";
import {
  setInitialDetails,
  setStep,
  setIsError,
} from "src/redux/slices/newProjectSetup";
import { useAppSelector } from "src/redux/store";
import { getStyles } from "src/styles/theme";
import {
  getDivisionOptions,
  getindustryOptions,
  getLandTypeOptions,
} from "src/utils/api/createProjectFlow";
import { optionTypeFormatter } from "src/utils/helper";
import FormWrapper from "../FormWrapper";
import defaultStyles, { StylesClasses } from "../NewForms/styles";
import { ProjectAreaDetailsForm } from "../newTypes";
import { getMyProfileData } from "src/utils/api/dashboard";
import { AxiosError, AxiosResponse } from "axios";
import { setGpsBoundaries } from "src/redux/slices/newProjectSetup";
import KmlOverlapChecker from "./KmlOverlapChecker";

const formHeads = formHeaders["ProjectAreaDetails"];

const PRE_REGISTRATION_CHECKLIST_PDF =
  "/Pre-Registration checklist for Identification and registration of Degraded Forest Land Parcels on GCP portal.pdf";

const ProjectDetails = () => {
  const { formData } = useAppSelector(
    (state) => state.root.selectNewProjectSetup
  );

  const projectData = useAppSelector((state) => state.root.projectById.data);
  const userType = useAppSelector((state) => state.root.user.data.userType);
  const userData = useAppSelector((state) => state.root.user.data);
  const stateOption = useAppSelector((state) => state?.root.state.allStates);
  const styles = getStyles<StylesClasses>(defaultStyles);
  const [pincodes, setPincodes] = useState([]);
  const dispatch = useDispatch();

  const user = useAppSelector((state) => state.root.user);

  const { data: myProfileData } = useQuery(
    ["myProfile", user.data.userId],
    () => getMyProfileData(user.data.userId),
    {
      onSuccess: (res: AxiosResponse) => {},
      onError: (err: AxiosError) => {},
      enabled: !!user.data.userId,
      refetchOnMount: true,
    }
  );

  const { control, getValues, setValue, formState, watch } =
    useForm<ProjectAreaDetailsForm>({
      mode: "onTouched",
      defaultValues: {
        ...formData.initialDetails,
        // Ensure stateName is set to stateId if user has one
        stateName:
          user?.data?.stateId ||
          myProfileData?.data?.currentAddress?.stateId ||
          formData.initialDetails.stateName,
      },
    });

  const { data: industryOption } = useQuery(
    "industryOption",
    () => getindustryOptions(),
    {
      refetchOnMount: true,
    }
  );

  const industryOptions = optionTypeFormatter(industryOption?.data);
  const stateOptions = optionTypeFormatter(stateOption);

  const { errors, isValid } = formState;

  const effectiveStateId =
    user?.data?.stateId || myProfileData?.data?.currentAddress?.stateId;

  const stateName = useMemo(() => {
    if (effectiveStateId && stateOption?.length) {
      const stateObj = _.find(stateOption, (i) => i.id === effectiveStateId);
      return stateObj?.name || "";
    }
    return "";
  }, [effectiveStateId, stateOption]);

  const district = useMemo(() => {
    const d = effectiveStateId || getValues("stateName");
    const dist = _.find(stateOption, (i) => i.id === d);
    return dist?.districts.length ? optionTypeFormatter(dist?.districts) : [];
  }, [watch("stateName"), effectiveStateId]);

  const { data: landTypeOptions } = useQuery("landTypeOption", () =>
    getLandTypeOptions()
  );

  const { data: division, error: divisionError } = useQuery(
    ["divisionsOption", user.data.stateId],
    () => getDivisionOptions(user.data.stateId),
    {
      enabled: !!(user.data.stateId && String(user.data.stateId).trim()),
      onError: (error: any) => {
        console.error(
          "Failed to load divisions for state:",
          user.data.stateId,
          error
        );
      },
    }
  );

  const userStateId =
    user?.data?.stateId || myProfileData?.data?.currentAddress?.stateId;

  const divisionoptions = myProfileData?.data?.implementingOfficerDetails?.map(
    (item) => {
      return {
        value: item.id,
        label: item.division,
      };
    }
  );

  const landCategoryOption = optionTypeFormatter(landTypeOptions?.data);

  //legal status of land

  const [legalStatusOfLand, setLegalStatusOfLand] = useState(
    "protectedForestLand"
  );
  const [legalStatusOfLandOther, setLegalStatusOfLandOther] = useState("");

  const finalLegalStatus =
    legalStatusOfLand === "others" ? legalStatusOfLandOther : legalStatusOfLand;

  //climaticZones
  const climaticZones = [
    "Tropical Wet (Humid) Climate",
    "Tropical Dry Climate",
    "Subtropical Humid Climate",
    "Mountain (Alpine) Climate",
    "Arid and Semi-Arid Climate",
  ];

  const [climaticZone, setClimaticZone] = useState("");

  const hasKml = !!formData.uploadedIds.gpsBoundaries.id;

  const handleNext = () => {
    const effectiveStateId =
      user?.data?.stateId || myProfileData?.data?.currentAddress?.stateId;

    if (!effectiveStateId || String(effectiveStateId).trim() === "") {
      dispatch(
        setIsError({
          isError: true,
          msg: "State information is missing. Please contact administrator to update your profile.",
        })
      );
      return;
    }

    setValue("division", user?.data?.userId);
    setValue("legalStatusOfLand", finalLegalStatus);
    setValue("climaticZone", climaticZone);
    setValue("stateName", effectiveStateId);
    const formValues = getValues();
    dispatch(setInitialDetails(formValues));
    dispatch(setStep("ProjectBaselineData"));
  };

  return (
    <FormWrapper
      isValid={isValid && hasKml}
      onNext={handleNext}
      onBack={() => {}}
      subHeading={formHeads.details}
      currentStepNumber={1}
      showBackBtn={false}
      showStepper={false}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mt: 2,
        }}
      >
        <Box {...styles("formTitle")}>{formHeads.title}</Box>
        <Button
          variant="contained"
          color="primary"
          endIcon={<DownloadIcon />}
          onClick={() => {
            window.open(PRE_REGISTRATION_CHECKLIST_PDF, "_blank");
          }}
          sx={{
            px: 2,
          }}
        >
          Pre-Registration checklist for Identification and registration of
          Degraded Forest Land Parcels on GCP portal
        </Button>
      </Box>
      <Box {...styles("inputContainer")}>
        {
          // todo
          // KmlOverlapChecker with original KML file uplad and new uploaded KML
        }

        <Box {...styles("input")}>
          <Input
            name="stateName"
            control={control}
            label="State"
            value={stateName}
            customStyles={{
              label: defaultStyles.label,
              wrapper: defaultStyles.selectWrapper,
            }}
            placeholder="State"
            readOnly={true}
          />
        </Box>
        <Box {...styles("input")}>
          <AutoComplete
            options={district.length ? district : []}
            rules={{ required: errorMessage.required }}
            customStyles={{
              label: defaultStyles.label,
              wrapper: defaultStyles.selectWrapper,
            }}
            name="districtName"
            control={control}
            placeholder="District"
            label="District"
          />
        </Box>

        <Box sx={{ width: "100%", mb: 1 }}>
          <KmlOverlapChecker
            onUploadSuccess={(id, url) =>
              dispatch(setGpsBoundaries({ id, url }))
            }
            existingUpload={
              formData.uploadedIds.gpsBoundaries.id
                ? formData.uploadedIds.gpsBoundaries
                : null
            }
          />
        </Box>

        {/* <Box {...styles("input")}>
          <AutoComplete
            options={divisionoptions.length ? divisionoptions : []}
            rules={{ required: errorMessage.required }}
            customStyles={{
              label: defaultStyles.label,
              wrapper: defaultStyles.selectWrapper,
            }}
            name="division"
            control={control}
            placeholder=" Enter Division"
            label="Division"
          />
        </Box> */}

        <Box {...styles("input")}>
          <Input
            customStyles={{
              label: defaultStyles.label,
              wrapper: defaultStyles.selectWrapper,
            }}
            name="circleName"
            rules={{ required: errorMessage.required }}
            control={control}
            errors={errors}
            placeholder="Circle"
            label="Circle"
            type="text"
          />
        </Box>
        <Box {...styles("input")}>
          <Input
            name="division"
            control={control}
            label="Division"
            value={user.data.division}
            customStyles={{
              label: defaultStyles.label,
              wrapper: defaultStyles.selectWrapper,
            }}
            placeholder="Enter Division"
            readOnly={true}
          />
        </Box>

        <Box {...styles("input")}>
          <AutoComplete
            options={landCategoryOption}
            customStyles={{
              label: defaultStyles.label,
              wrapper: defaultStyles.selectWrapper,
            }}
            name="kindOfDegradedLand"
            rules={{ required: errorMessage.required }}
            control={control}
            placeholder="Select"
            label="Select the type of Degraded Land"
            readOnly={true}
          />
        </Box>

        <Box {...styles("input")}>
          <Input
            customStyles={{
              label: defaultStyles.label,
              wrapper: defaultStyles.selectWrapper,
            }}
            rules={{ required: errorMessage.required }}
            name="siteName"
            control={control}
            errors={errors}
            placeholder="Name of site"
            label="Name of site"
            type="text"
          />
        </Box>

        <Box {...styles("input")}>
          <Input
            customStyles={{
              label: defaultStyles.label,
              wrapper: defaultStyles.selectWrapper,
            }}
            name="revenueBlock"
            control={control}
            errors={errors}
            rules={{ required: errorMessage.required }}
            placeholder="Write Revenue Block name"
            label="Revenue Block"
            type="text"
          />
        </Box>

        <Box {...styles("input")}>
          <Input
            customStyles={{
              label: defaultStyles.label,
              wrapper: defaultStyles.selectWrapper,
            }}
            name="range"
            rules={{ required: errorMessage.required }}
            control={control}
            errors={errors}
            placeholder="Write the name of Forest Range"
            label="Forest Range "
          />
        </Box>

        <Box {...styles("input")}>
          <Input
            customStyles={{
              label: defaultStyles.label,
              wrapper: defaultStyles.selectWrapper,
            }}
            name="compartment"
            control={control}
            errors={errors}
            placeholder="Enter your Compartment No"
            label="Compartment No"
            type="number"
            rules={{
              required: errorMessage.required,
              validate: (value) =>
                Number(value) >= 0 ||
                "Compartment No must be a positive number",
            }}
          />
        </Box>

        <Box {...styles("input")}>
          <Input
            customStyles={{
              label: defaultStyles.label,
              wrapper: defaultStyles.selectWrapper,
            }}
            name="beta"
            control={control}
            rules={{ required: errorMessage.required }}
            errors={errors}
            placeholder="Enter your Beat"
            label="Beat"
          />
        </Box>
        <Box {...styles("input")}>
          <Input
            customStyles={{
              label: defaultStyles.label,
              wrapper: defaultStyles.selectWrapper,
            }}
            name="khasraNumber"
            control={control}
            errors={errors}
            rules={{
              required: errorMessage.required,
              validate: (value) =>
                Number(value) >= 0 || "Survey No must be a positive number",
            }}
            type="number"
            placeholder="Enter your Survey No"
            label="Survey No"
          />
        </Box>

        <Box {...styles("input")}>
          <Input
            customStyles={{
              label: defaultStyles.label,
              wrapper: defaultStyles.selectWrapper,
            }}
            name="treeCanopyDensity"
            rules={{
              max: {
                value: 0.2,
                message:
                  "Tree canopy density value should not be greater than 0.20",
              },
              validate: (value) =>
                Number(value) >= 0 ||
                "Tree canopy density must be a positive number",
              required: errorMessage.required,
            }}
            control={control}
            errors={errors}
            placeholder="Enter your Tree Canopy Density. Exp: 0.2"
            label="Tree Canopy Density"
            type="number"
          />
          <Box
            sx={{
              color: "#272727",
              fontSize: "11px",
              fontWeight: "400",
              position: "relative",
              bottom: !!errors.treeCanopyDensity ? "-4px" : "10px",
            }}
          >
            Enter your Tree Canopy Density (in percentage between 0 - 0.2)
          </Box>
        </Box>

        <Box {...styles("input")}>
          <Input
            customStyles={{
              label: defaultStyles.label,
              wrapper: defaultStyles.selectWrapper,
            }}
            name="areaSize"
            rules={{
              required: errorMessage.required,
              validate: (value) =>
                Number(value) >= 5 || "Area should be greater than 5 hectare",
            }}
            control={control}
            errors={errors}
            placeholder="Not Less Then 5 Hectares"
            label="Total Land Area (in Hectares) "
            type="number"
          />
          <Box
            sx={{
              color: "#272727",
              fontSize: "11px",
              fontWeight: "400",
              position: "relative",
              bottom: !!errors.areaSize ? "-4px" : "10px",
            }}
          >
            (Enter the Total Land Area Size (in Hectares). Must be 5 or more.)
          </Box>
        </Box>

        {/* <Box {...styles("input")}>
          <Input
            customStyles={{
              label: defaultStyles.label,
              wrapper: defaultStyles.selectWrapper,
            }}
            rules={{ required: errorMessage.required }}
            name="landUseCategory"
            control={control}
            errors={errors}
            type="text"
            placeholder="Enter Land Use Category"
            label="Land Use Category"
          />
        </Box> */}

        {/* <Box {...styles("input")}>
          <Input
            customStyles={{
              label: defaultStyles.label,
              wrapper: defaultStyles.selectWrapper,
            }}
            name="legalStatusOfLand"
            control={control}
            errors={errors}
            type="text"
            placeholder="Legal Status of Land"
            label="Legal Status of Land"
          />
        </Box> */}

        <Box sx={{ pt: 6 }} {...styles("input")}>
          {/* Label */}
          <Grid item xs={12}>
            <Typography sx={{ fontWeight: 600, pt: 2, mb: 0.5 }}>
              Legal Status of Land
            </Typography>
          </Grid>

          {/* Dropdown */}
          <Grid item xs={4}>
            <FormControl fullWidth>
              <Select
                value={legalStatusOfLand}
                onChange={(e) => {
                  setLegalStatusOfLand(e.target.value);
                  if (e.target.value !== "others") {
                    setLegalStatusOfLandOther("");
                  }
                }}
                sx={{ backgroundColor: "#fff" }}
              >
                <MenuItem value="protectedForestLand">
                  Protected forest land
                </MenuItem>
                <MenuItem value="reserveForestLand">
                  Reserve forest land
                </MenuItem>
                <MenuItem value="others">Others</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Show TextField only when Others */}
          {legalStatusOfLand === "others" && (
            <Grid item xs={8}>
              <TextField
                fullWidth
                required
                placeholder="Enter Legal Status of Land"
                value={legalStatusOfLandOther}
                onChange={(e) => setLegalStatusOfLandOther(e.target.value)}
                multiline
                minRows={1}
              />
            </Grid>
          )}
        </Box>

        {/*slop*/}

        <Box {...styles("input")}>
          <Input
            customStyles={{
              label: defaultStyles.label,
              wrapper: defaultStyles.selectWrapper,
            }}
            name="slope"
            control={control}
            errors={errors}
            rules={{ required: errorMessage.required }}
            type="text"
            placeholder="e.g: plain, hilly"
            label="Slope"
          />
        </Box>

        <Box {...styles("input")}>
          <Input
            customStyles={{
              label: defaultStyles.label,
              wrapper: defaultStyles.selectWrapper,
            }}
            name="terrain"
            control={control}
            errors={errors}
            rules={{ required: errorMessage.required }}
            type="text"
            placeholder="e.g: slope %"
            label="Terrain "
          />
        </Box>

        <Box {...styles("input")}>
          {/* Label same as INPUT label */}
          <Grid item xs={12}>
            <Typography sx={{ fontWeight: 600, pt: 2, mb: 0.5 }}>
              Climatic Zone
            </Typography>
          </Grid>

          {/* Dropdown styled same as INPUT wrapper */}
          <Box sx={defaultStyles.selectWrapper}>
            <FormControl fullWidth>
              <Select
                value={climaticZone}
                onChange={(e) => setClimaticZone(e.target.value)}
                displayEmpty
                sx={{ backgroundColor: "#fff" }}
              >
                <MenuItem value="" disabled>
                  Select Climatic Zone
                </MenuItem>

                <MenuItem value="Tropical Wet (Humid) Climate">
                  Tropical Wet (Humid) Climate
                </MenuItem>
                <MenuItem value="Tropical Dry Climate">
                  Tropical Dry Climate
                </MenuItem>
                <MenuItem value="Subtropical Humid Climate">
                  Subtropical Humid Climate
                </MenuItem>
                <MenuItem value="Mountain (Alpine) Climate">
                  Mountain (Alpine) Climate
                </MenuItem>
                <MenuItem value="Arid and Semi-Arid Climate">
                  Arid and Semi-Arid Climate
                </MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>

        {/* <Box {...styles("input")}>
          <Input
            customStyles={{
              label: defaultStyles.label,
              wrapper: defaultStyles.selectWrapper,
            }}
            name="availableInspectionPaths"
            control={control}
            errors={errors}
            type="text"
            placeholder="Available inspection paths"
            label="Available inspection paths"
          />
        </Box> */}

        <Box {...styles("input")}>
          <Input
            customStyles={{
              label: defaultStyles.label,
              wrapper: defaultStyles.selectWrapper,
            }}
            name="accessibility"
            control={control}
            errors={errors}
            rules={{ required: errorMessage.required }}
            type="text"
            placeholder="e.g: near road, distance, approach route"
            label="Accessibility with Distance"
          />
        </Box>
      </Box>
    </FormWrapper>
  );
};

export default ProjectDetails;
